// =============================================================================
// Legacy Hub Adapter
// Reads and normalizes data from indxone.hub.v1
// =============================================================================

import { createDefaultWorkspace } from '@shared/storage';
import { Workspace, ProjectContext, Evaluation, Sipoc, Moscow, Raci, CraMonth, CraDayState, TreasuryProfile, TreasuryCharge } from '@shared/types';
import { averageEvaluationScore, recommendationsFromScore, verdictFromScore } from '@shared/domain';
import { generateId } from '@shared/utils';

/**
 * Legacy Hub state structure (as stored in localStorage)
 */
interface LegacyHubV1 {
  schema: number;
  project: {
    name: string;
    client: string;
    owner: string;
  };
  eval?: { data: unknown; meta: { ts: number; v: number } };
  sipoc?: { data: unknown; meta: { ts: number; v: number } };
  raci?: { data: unknown; meta: { ts: number; v: number } };
  moscow?: { data: unknown; meta: { ts: number; v: number } };
  calc?: { data: unknown; meta: { ts: number; v: number } };
  progress: Record<string, boolean>;
}

const STORAGE_KEY = 'indxone.hub.v1';
const LEGACY_EVAL_PROJECT_KEY = 'eval-mission.project';
const LEGACY_MISSIO_KEY = 'indxone.missio.v1';
const LEGACY_CRA_KEY = 'indxone.cra.v1';
const LEGACY_TREASURY_KEY = 'indxone.tresorerie.v1';
const LEGACY_RUNWAY_KEY = 'indxone.runway.v2';

type LegacyEvalProject = {
  client?: string;
  name?: string;
  scope?: string;
  budget?: string;
  tjm?: string;
};

type LegacyMissio = {
  profil?: { nom?: string; prenom?: string };
  settings?: { tjm?: number; jours?: number; status?: 'sasu' | 'ei' | 'eurl' | 'portage' };
  missions?: Array<{
    clientNom?: string;
    titre?: string;
    notes?: string;
    budgetHT?: number;
    tjm?: number;
  }>;
};

type LegacyCra = {
  dayStates?: Record<string, CraDayState>;
  meta?: {
    consultant?: string;
    client?: string;
    mission?: string;
    tjm?: number;
  };
};

type LegacyTreasury = {
  charges?: Array<{ id?: string; label?: string; amount?: number }>;
};

type LegacyRunway = {
  solde?: number;
  charges?: number;
  ca?: number;
  occ?: number;
};

/**
 * Load legacy hub data from localStorage
 */
function loadLegacyHub(): LegacyHubV1 | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as LegacyHubV1;
  } catch {
    return null;
  }
}

/**
 * Normalize legacy project data to new format
 */
function normalizeProject(legacy: LegacyHubV1['project']): ProjectContext {
  return {
    id: generateId(),
    name: legacy.name || 'Nouveau Projet',
    client: legacy.client || '',
    owner: legacy.owner || '',
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
}

function loadLegacyEvalProject(): LegacyEvalProject | null {
  try {
    const raw = localStorage.getItem(LEGACY_EVAL_PROJECT_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as LegacyEvalProject;
  } catch {
    return null;
  }
}

function loadLegacyMissio(): LegacyMissio | null {
  try {
    const raw = localStorage.getItem(LEGACY_MISSIO_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as LegacyMissio;
  } catch {
    return null;
  }
}

function loadLegacyCra(): LegacyCra | null {
  try {
    const raw = localStorage.getItem(LEGACY_CRA_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as LegacyCra;
  } catch {
    return null;
  }
}

function loadLegacyTreasury(): LegacyTreasury | null {
  try {
    const raw = localStorage.getItem(LEGACY_TREASURY_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as LegacyTreasury;
  } catch {
    return null;
  }
}

function loadLegacyRunway(): LegacyRunway | null {
  try {
    const raw = localStorage.getItem(LEGACY_RUNWAY_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as LegacyRunway;
  } catch {
    return null;
  }
}

function toNumber(value: unknown): number | undefined {
  const num = Number(value);
  return Number.isFinite(num) ? num : undefined;
}

function unwrapLegacyToolData<T>(legacyData: unknown): T | null {
  if (!legacyData || typeof legacyData !== 'object') return null;
  const candidate = legacyData as { data?: unknown };
  if (candidate.data && typeof candidate.data === 'object') {
    return candidate.data as T;
  }
  return legacyData as T;
}

/**
 * Normalize legacy evaluation data
 * Placeholder - will be fully implemented based on actual legacy format
 */
function normalizeEvaluation(legacyData: unknown): Evaluation | null {
  const raw = unwrapLegacyToolData<Record<string, unknown>>(legacyData);
  if (!raw) return null;
  const totalScore = toNumber(raw.score) ?? 0;
  const verdictLabel = String(raw.verdict || '');

  const scores = {
    strategicAlignment: Math.max(1, Math.min(5, Math.round(totalScore / 20) || 1)),
    technicalComplexity: Math.max(1, Math.min(5, Math.round(totalScore / 20) || 1)),
    teamReadiness: Math.max(1, Math.min(5, Math.round(totalScore / 20) || 1)),
    businessValue: Math.max(1, Math.min(5, Math.round(totalScore / 20) || 1)),
    riskLevel: Math.max(1, Math.min(5, 6 - (Math.round(totalScore / 20) || 1))),
  };

  const derivedScore = totalScore || averageEvaluationScore(scores);
  const verdict =
    verdictLabel === 'GO'
      ? 'GO'
      : verdictLabel === 'NO-GO'
        ? 'NO_GO'
        : verdictFromScore(derivedScore);

  return {
    id: generateId(),
    projectId: '',
    scores,
    verdict,
    totalScore: derivedScore,
    comments: [raw.scope, raw.mode].filter(Boolean).join(' · ') || undefined,
    recommendations: recommendationsFromScore(derivedScore),
    calculatedAt: Date.now(),
    meta: {
      version: 1,
      calculatedBy: 'legacy-hub',
    },
  };
}

/**
 * Normalize legacy SIPOC data
 * Placeholder - will be fully implemented based on actual legacy format
 */
function normalizeSipoc(legacyData: unknown): Sipoc | null {
  const raw = unwrapLegacyToolData<Record<string, unknown>>(legacyData);
  if (!raw) return null;
  const columns = raw as Record<string, string[]>;
  const toItems = (values: unknown) =>
    (Array.isArray(values) ? values : [])
      .map((value, index) => (String(value || '').trim() ? {
        id: generateId(),
        title: String(value).trim(),
        order: index,
      } : null))
      .filter((item): item is { id: string; title: string; order: number } => Boolean(item));

  return {
    id: generateId(),
    projectId: '',
    name: '',
    owner: raw.owner ? String(raw.owner) : undefined,
    suppliers: { items: toItems(columns.S) },
    inputs: { items: toItems(columns.I) },
    process: { items: toItems(columns.P) },
    outputs: { items: toItems(columns.O) },
    customers: { items: toItems(columns.C) },
    projectScope: raw.scope ? String(raw.scope) : undefined,
    scope: 'SIMPLE',
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
}

/**
 * Normalize legacy MoSCoW data
 * Placeholder - will be fully implemented based on actual legacy format
 */
function normalizeMoscow(legacyData: unknown): Moscow | null {
  const raw = unwrapLegacyToolData<Record<string, unknown>>(legacyData);
  const features = Array.isArray(raw?.features) ? raw.features : [];
  if (!raw || !features.length) return null;

  const toItems = (cat: string) =>
    features
      .filter((feature) => feature && typeof feature === 'object' && (feature as { cat?: string }).cat === cat)
      .map((feature, index) => {
        const item = feature as { name?: string; effort?: number };
        return {
          id: generateId(),
          title: item.name || `Item ${index + 1}`,
          effort: Number(item.effort) || 1,
          order: index,
        };
      });

  return {
    id: generateId(),
    projectId: '',
    name: typeof raw.projectName === 'string' ? raw.projectName : '',
    budgetDays: toNumber(raw.budgetDays),
    teamSize: toNumber(raw.teamSize),
    categories: {
      must: { items: toItems('M') },
      should: { items: toItems('S') },
      could: { items: toItems('C') },
      wont: { items: toItems('W') },
    },
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
}

/**
 * Normalize legacy RACI data
 * Placeholder - will be fully implemented based on actual legacy format
 */
function normalizeRaci(legacyData: unknown): Raci | null {
  const raw = unwrapLegacyToolData<Record<string, unknown>>(legacyData);
  const rolesInput = Array.isArray(raw?.roles) ? raw.roles : [];
  const tasksInput = Array.isArray(raw?.tasks) ? raw.tasks : [];
  if (!raw || (!rolesInput.length && !tasksInput.length)) return null;

  const roles = rolesInput.map((role, index) => ({
    id: `legacy-role-${index}`,
    name: String(role || `Role ${index + 1}`),
  }));

  const tasks = tasksInput.map((task, index) => {
    const item = task as { name?: string };
    return {
      id: `legacy-task-${index}`,
      title: item.name || `Task ${index + 1}`,
      order: index,
    };
  });

  const matrix = tasksInput.flatMap((task, taskIndex) => {
    const item = task as { assignments?: string[] };
    return (item.assignments || [])
      .map((assignment, roleIndex) => {
        if (!assignment || !['R', 'A', 'C', 'I'].includes(assignment)) return null;
        return {
          taskId: `legacy-task-${taskIndex}`,
          roleId: `legacy-role-${roleIndex}`,
          assignment: assignment as 'R' | 'A' | 'C' | 'I',
        };
      })
      .filter((entry): entry is { taskId: string; roleId: string; assignment: 'R' | 'A' | 'C' | 'I' } => Boolean(entry));
  });

  return {
    id: generateId(),
    projectId: '',
    name: typeof raw.projectName === 'string' ? raw.projectName : '',
    roles,
    tasks,
    matrix,
    sponsor: typeof raw.sponsor === 'string' ? raw.sponsor : undefined,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
}

function buildProjectFromEvalProject(legacy: LegacyEvalProject): ProjectContext | null {
  if (!legacy.name && !legacy.client) return null;
  return {
    id: generateId(),
    name: legacy.name || 'Mission sans nom',
    client: legacy.client || '',
    owner: '',
    scope: legacy.scope || '',
    budget: toNumber(legacy.budget),
    tjm: toNumber(legacy.tjm),
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
}

function buildProjectFromMissio(legacy: LegacyMissio): ProjectContext | null {
  const mission = legacy.missions?.[0];
  if (!mission && !legacy.profil) return null;
  return {
    id: generateId(),
    name: mission?.titre || 'Mission Missio',
    client: mission?.clientNom || '',
    owner: [legacy.profil?.prenom, legacy.profil?.nom].filter(Boolean).join(' ') || '',
    description: mission?.notes || '',
    budget: mission?.budgetHT,
    tjm: mission?.tjm || legacy.settings?.tjm,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
}

function buildProfileFromLegacy(legacyMissio: LegacyMissio | null) {
  const base = createDefaultWorkspace().profile;
  return {
    ...base,
    firstName: legacyMissio?.profil?.prenom || base.firstName,
    lastName: legacyMissio?.profil?.nom || base.lastName,
    company: legacyMissio?.missions?.[0]?.clientNom || base.company,
    status: legacyMissio?.settings?.status || base.status,
    tjm: Number(legacyMissio?.settings?.tjm || legacyMissio?.missions?.[0]?.tjm || base.tjm) || base.tjm,
    daysPerMonth: Number(legacyMissio?.settings?.jours || base.daysPerMonth) || base.daysPerMonth,
    updatedAt: Date.now(),
  };
}

function normalizeLegacyCra(legacy: LegacyCra | null, projectId: string): CraMonth[] {
  if (!legacy?.dayStates) return [];
  const grouped = new Map<string, Record<string, CraDayState>>();

  Object.entries(legacy.dayStates).forEach(([dateKey, state]) => {
    if (!state) return;
    const [year, month] = dateKey.split('-');
    if (!year || !month) return;
    const monthKey = `${year}-${month}`;
    const record = grouped.get(monthKey) || {};
    record[dateKey] = state;
    grouped.set(monthKey, record);
  });

  return Array.from(grouped.entries()).map(([monthKey, dayStates]) => {
    const [yearText, monthText] = monthKey.split('-');
    const year = Number(yearText);
    const month = Number(monthText) - 1;
    let workedDays = 0;
    let leaveDays = 0;
    Object.values(dayStates).forEach((state) => {
      if (state === 'travaille') workedDays += 1;
      if (state === 'demi') workedDays += 0.5;
      if (state === 'conge') leaveDays += 1;
    });
    const tjm = Number(legacy.meta?.tjm) || 0;

    return {
      id: generateId(),
      projectId,
      year,
      month,
      consultant: legacy.meta?.consultant || '',
      client: legacy.meta?.client || '',
      mission: legacy.meta?.mission || '',
      tjm,
      dayStates,
      workedDays,
      leaveDays,
      revenue: workedDays * tjm,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
  });
}

function normalizeLegacyCharges(legacy: LegacyTreasury | null, fallbackAmount?: number): TreasuryCharge[] {
  const rawCharges = Array.isArray(legacy?.charges) ? legacy.charges : [];
  const normalized = rawCharges
    .map((charge, index) => ({
      id: charge.id || `legacy-charge-${index}`,
      label: String(charge.label || '').trim() || `Charge ${index + 1}`,
      amount: Number(charge.amount) || 0,
    }))
    .filter((charge) => charge.amount > 0 || charge.label);

  if (normalized.length) return normalized;
  if (fallbackAmount && fallbackAmount > 0) {
    return [{ id: generateId(), label: 'Charges mensuelles', amount: fallbackAmount }];
  }

  return [
    { id: generateId(), label: 'Mutuelle', amount: 150 },
    { id: generateId(), label: 'Comptable', amount: 120 },
    { id: generateId(), label: 'Logiciels / abonnements', amount: 80 },
  ];
}

function buildTreasuryFromLegacy(
  project: ProjectContext,
  legacyMissio: LegacyMissio | null,
  legacyTreasury: LegacyTreasury | null,
  legacyRunway: LegacyRunway | null,
  legacyCraMonths: CraMonth[],
): TreasuryProfile | null {
  const settings = legacyMissio?.settings || {};
  const latestCra = [...legacyCraMonths].sort((a, b) => (b.year * 100 + b.month) - (a.year * 100 + a.month))[0];
  const tjm = Number(project.tjm || settings.tjm || latestCra?.tjm || 0);
  const daysPerMonth = Number(settings.jours || latestCra?.workedDays || (legacyRunway?.ca && tjm > 0 ? legacyRunway.ca / tjm : 18) || 18);
  const openingBalance = Number(legacyRunway?.solde || 5000);
  const charges = normalizeLegacyCharges(legacyTreasury, Number(legacyRunway?.charges));
  const status = settings.status || 'sasu';

  if (!legacyTreasury && !legacyRunway && !legacyMissio && !latestCra) {
    return null;
  }

  return {
    id: generateId(),
    projectId: project.id,
    status,
    openingBalance,
    caYtd: latestCra?.revenue || Number(legacyRunway?.ca) || 0,
    horizonMonths: 6,
    daysPerMonth: Number.isFinite(daysPerMonth) && daysPerMonth > 0 ? Math.round(daysPerMonth * 10) / 10 : 18,
    tjm,
    charges,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
}

/**
 * Migrate legacy hub data to new workspace format
 * This is idempotent - can be called multiple times safely
 */
export function migrateLegacyHubToWorkspace(existingWorkspace?: Workspace): Workspace {
  const legacy = loadLegacyHub();
  const legacyEvalProject = loadLegacyEvalProject();
  const legacyMissio = loadLegacyMissio();
  const legacyCra = loadLegacyCra();
  const legacyTreasury = loadLegacyTreasury();
  const legacyRunway = loadLegacyRunway();
  
  if (!legacy && !legacyEvalProject && !legacyMissio && !legacyCra && !legacyTreasury && !legacyRunway) {
    // No legacy data to migrate
    return existingWorkspace || createDefaultWorkspace();
  }

  // Check if already migrated
  const alreadyMigrated = existingWorkspace?.meta.migratedFrom?.includes('indxone.hub.v1');
  
  if (alreadyMigrated) {
    return existingWorkspace as Workspace;
  }

  // Create new workspace from legacy data
  const project = legacy
    ? normalizeProject(legacy.project)
    : buildProjectFromEvalProject(legacyEvalProject || {}) || buildProjectFromMissio(legacyMissio || {}) || {
      id: generateId(),
      name: 'Nouveau Projet',
      client: '',
      owner: '',
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
  
  const evaluations: Evaluation[] = [];
  const sipocs: Sipoc[] = [];
  const moscows: Moscow[] = [];
  const racis: Raci[] = [];
  const cras: CraMonth[] = normalizeLegacyCra(legacyCra, project.id);
  const treasury = buildTreasuryFromLegacy(project, legacyMissio, legacyTreasury, legacyRunway, cras);

  // Normalize each module if it exists
  if (legacy?.eval?.data) {
    const evalData = normalizeEvaluation(legacy.eval.data);
    if (evalData) evaluations.push({ ...evalData, projectId: project.id });
  }
  
  if (legacy?.sipoc?.data) {
    const sipocData = normalizeSipoc(legacy.sipoc.data);
    if (sipocData) sipocs.push({ ...sipocData, projectId: project.id, name: sipocData.name || project.name, owner: sipocData.owner || project.owner, projectScope: sipocData.projectScope || project.scope });
  }
  
  if (legacy?.moscow?.data) {
    const moscowData = normalizeMoscow(legacy.moscow.data);
    if (moscowData) moscows.push({ ...moscowData, projectId: project.id, name: moscowData.name || project.name });
  }
  
  if (legacy?.raci?.data) {
    const raciData = normalizeRaci(legacy.raci.data);
    if (raciData) racis.push({ ...raciData, projectId: project.id, name: raciData.name || project.name, sponsor: raciData.sponsor || project.owner || project.client });
  }

  // Build migration record
  const migrationRecords = [
    {
      source: 'indxone.hub.v1',
      timestamp: Date.now(),
      success: true,
      message: 'Migrated from legacy hub format',
    },
  ];

  const migratedFrom = Array.from(new Set([
    ...(existingWorkspace?.meta.migratedFrom || []),
    ...(legacy ? ['indxone.hub.v1'] : []),
    ...(legacyEvalProject ? ['eval-mission.project'] : []),
    ...(legacyMissio ? ['indxone.missio.v1'] : []),
    ...(legacyCra ? ['indxone.cra.v1'] : []),
    ...(legacyTreasury ? ['indxone.tresorerie.v1'] : []),
    ...(legacyRunway ? ['indxone.runway.v2'] : []),
  ]));

  return {
    ...createDefaultWorkspace(),
    meta: {
      schema: 1,
      version: '1.0.0',
      createdAt: existingWorkspace?.meta.createdAt || Date.now(),
      updatedAt: Date.now(),
      migratedFrom,
      migrationRecords: [...(existingWorkspace?.meta.migrationRecords || []), ...migrationRecords],
    },
    profile: buildProfileFromLegacy(legacyMissio),
    currentProjectId: project.id,
    projects: [project],
    evaluations,
    sipocs,
    moscows,
    racis,
    dealRooms: [],
    cras,
    treasuries: treasury ? [treasury] : [],
  };
}

/**
 * Check if legacy hub data exists
 */
export function hasLegacyHubData(): boolean {
  return loadLegacyHub() !== null;
}

/**
 * Get raw legacy hub data (for debugging/inspection)
 */
export function getLegacyHubData() {
  return loadLegacyHub();
}
